import {
    Box,
    Button,
    CloseButton,
    Drawer,
    Flex,
    Icon,
    IconButton,
    Popover,
    Portal,
    Separator,
    Text,
} from "@chakra-ui/react";
import { CaretDownIcon, FadersIcon } from "@phosphor-icons/react/dist/ssr";
import { useEffect, useState } from "react";

import { Tooltip } from "@/components/ui/tooltip";
import { DEFAULT_OPERATION } from "@/pages/Buscar/useSearchState";

import { RangeBody } from "./_RangeBody";
import { useRangeDraft } from "./_useRangeDraft";
import { BedsBathsPopoverBody, useBedsBathsDraft } from "./BedsBathsPopover";
import { OperationFilterBody, useOperationDraft } from "./OperationFilter";
import { PricePopoverBody, usePriceDraft } from "./PricePopover";
import { PropertyTypePopoverBody, usePropertyTypeDraft } from "./PropertyTypePopover";

const INCLUDE_OPTIONS = [
    { value: "both", label: "Ambos" },
    { value: "propiedades", label: "Solo propiedades" },
    { value: "desarrollos", label: "Solo desarrollos" },
];

const SIZE_BOUNDS = { min: 0, max: 10_000, step: 1 };
const getSizeBounds = () => SIZE_BOUNDS;

// Slider min/max labels carry the "m²" unit; inputs use bare numbers to keep
// typing uncluttered.
const formatSizeLabel = (value, atMax) => `${value.toLocaleString("es-MX")} m²${atMax ? "+" : ""}`;
const formatSizeInputValue = (value, isEmpty) => (isEmpty ? "" : value.toLocaleString("es-MX"));

export const useMoreFiltersDraft = (state) => {
    // groundSize range is delegated to the shared range-filter hook;
    // includeMode is a sibling toggle that's section-specific.
    const sizeDraft = useRangeDraft({
        state,
        key: "groundSize",
        getBounds: getSizeBounds,
    });

    const [draftIncluir, setDraftIncluir] = useState(state.includeMode);
    useEffect(() => {
        setDraftIncluir(state.includeMode);
    }, [state.includeMode]);
    const incluirDirty = draftIncluir !== state.includeMode;

    const isDirty = sizeDraft.isDirty || incluirDirty;

    const commit = (setFilter, clearFilter) => {
        sizeDraft.commit(setFilter, clearFilter);
        if (draftIncluir === "both") clearFilter("includeMode");
        else setFilter("includeMode", draftIncluir);
    };

    const reset = (setFilter, clearFilter) => {
        sizeDraft.reset(setFilter, clearFilter);
        setDraftIncluir("both");
        clearFilter("includeMode");
    };

    return {
        // Expose the shared size draft for SuperficieFilterBody to forward
        // to RangeBody. Wrappers don't reach into its internals — they only
        // read sizeDirty/incluirDirty/isDirty and call commit/reset.
        sizeDraft,
        draftIncluir,
        setDraftIncluir,
        sizeDirty: sizeDraft.isDirty,
        incluirDirty,
        isDirty,
        canCommit: true,
        commit,
        reset,
    };
};

// Just the Superficie slider + min/max inputs. Thin shim over the shared
// RangeBody — formatters carry the m² unit, input height stays at the
// drawer-friendly 36px the layout was already tuned for.
export const SuperficieFilterBody = ({ draft }) => (
    <RangeBody
        draft={draft.sizeDraft}
        formatLabel={formatSizeLabel}
        formatInputValue={formatSizeInputValue}
        inputHeight="36px"
    />
);

// Just the Incluir segmented control.
export const IncluirFilterBody = ({ draft }) => {
    const { draftIncluir, setDraftIncluir } = draft;
    return (
        <Flex borderWidth="1px" borderColor="gray.200" borderRadius="md" overflow="hidden">
            {INCLUDE_OPTIONS.map((opt) => {
                const active = opt.value === draftIncluir;
                return (
                    <Button
                        key={opt.value}
                        onClick={() => setDraftIncluir(opt.value)}
                        flex={1}
                        h="36px"
                        fontSize="xs"
                        fontWeight={active ? 600 : 500}
                        bg={active ? "colorPalette.solid" : "white"}
                        color={active ? "white" : "gray.700"}
                        borderRadius={0}
                        _hover={{ bg: active ? "colorPalette.solid" : "gray.50" }}
                    >
                        {opt.label}
                    </Button>
                );
            })}
        </Flex>
    );
};

const triggerProps = (pillActive) => ({
    h: "40px",
    minW: "140px",
    px: 4,
    fontSize: "sm",
    fontWeight: 500,
    bg: pillActive ? "colorPalette.solid" : "white",
    color: pillActive ? "white" : "gray.700",
    borderWidth: "1px",
    borderColor: pillActive ? "colorPalette.solid" : "gray.200",
    borderRadius: "md",
    _hover: { bg: pillActive ? "colorPalette.solid" : "gray.50" },
});

const Section = ({ title, isDirty, children }) => (
    <Box>
        <Text fontSize="md" fontWeight={600} color="gray.800" mb={3}>
            {title}
            {isDirty ? " •" : ""}
        </Text>
        {children}
    </Box>
);

// Shared drawer body — used by both xs (bottom) and sm-lg (lateral) variants.
// `operationDraft` is optional: only the xs bottom drawer absorbs the
// Comprar/Rentar/Preventa/Todas operation segmented; at sm-lg the segmented
// stays visible in TopBar.
const AbsorbedDrawerContents = ({
    onClose,
    operationDraft,
    priceDraft,
    bedsBathsDraft,
    propertyTypeDraft,
    ownDraft,
    state,
    setFilter,
    clearFilter,
}) => {
    const drafts = [operationDraft, priceDraft, bedsBathsDraft, propertyTypeDraft, ownDraft].filter(
        Boolean
    );
    const canCommitAll = drafts.every((d) => d.canCommit);
    const applyAll = () => {
        drafts.forEach((d) => d.commit(setFilter, clearFilter));
        onClose();
    };
    const clearAll = () => {
        drafts.forEach((d) => d.reset(setFilter, clearFilter));
        onClose();
    };

    return (
        <>
            {/* Explicit px/py because Chakra v3's Drawer.Header ships with
                minimal default padding — the title and close button
                otherwise sit flush against the drawer edges. CloseTrigger
                lives inside the header Flex (not at Drawer.Content root)
                so it sits ABOVE the divider, not floating on top of it. */}
            <Drawer.Header borderBottomWidth="1px" borderColor="gray.100" px={4} py={3}>
                <Flex justify="space-between" align="center" w="100%">
                    <Drawer.Title fontSize="lg" fontWeight={600} color="gray.800">
                        Filtros
                    </Drawer.Title>
                    <Drawer.CloseTrigger asChild>
                        <CloseButton size="sm" position="static" />
                    </Drawer.CloseTrigger>
                </Flex>
            </Drawer.Header>
            <Drawer.Body p={4}>
                {operationDraft && (
                    <>
                        <Section title="Operación" isDirty={operationDraft.isDirty}>
                            <OperationFilterBody draft={operationDraft} />
                        </Section>
                        <Separator my={5} />
                    </>
                )}
                <Section title="Precio" isDirty={priceDraft.isDirty}>
                    <PricePopoverBody draft={priceDraft} />
                </Section>
                <Separator my={5} />
                <Section title="Tipo de propiedad" isDirty={propertyTypeDraft.isDirty}>
                    <PropertyTypePopoverBody draft={propertyTypeDraft} />
                </Section>
                <Separator my={5} />
                <Section title="Características" isDirty={bedsBathsDraft.isDirty}>
                    <BedsBathsPopoverBody draft={bedsBathsDraft} committed={state} />
                </Section>
                <Separator my={5} />
                <Section title="Superficie del terreno (m²)" isDirty={ownDraft.sizeDirty}>
                    <SuperficieFilterBody draft={ownDraft} />
                </Section>
                <Separator my={5} />
                <Section title="Incluir" isDirty={ownDraft.incluirDirty}>
                    <IncluirFilterBody draft={ownDraft} />
                </Section>
            </Drawer.Body>
            {/* Match the popover footer's geometry: same px/py as the body,
                same border-top, same button sizes — so the drawer's
                Limpiar/Aplicar bar reads as the same control across all
                three variants. */}
            <Drawer.Footer borderTopWidth="1px" borderColor="gray.100" px={4} py={3}>
                <Flex justify="space-between" gap={2} w="100%">
                    <Button
                        onClick={clearAll}
                        variant="ghost"
                        size="sm"
                        px={5}
                        borderRadius="md"
                        color="gray.600"
                    >
                        Limpiar todos
                    </Button>
                    <Tooltip
                        content="Selecciona al menos un tipo de propiedad para aplicar."
                        disabled={canCommitAll}
                        openDelay={150}
                    >
                        <Box>
                            <Button
                                onClick={applyAll}
                                disabled={!canCommitAll}
                                size="sm"
                                px={6}
                                borderRadius="md"
                                bg="colorPalette.solid"
                                color="white"
                                _hover={{ bg: "colorPalette.emphasized" }}
                                _disabled={{
                                    bg: "gray.300",
                                    color: "gray.500",
                                    cursor: "not-allowed",
                                }}
                            >
                                Aplicar
                            </Button>
                        </Box>
                    </Tooltip>
                </Flex>
            </Drawer.Footer>
        </>
    );
};

const MoreFiltersPopover = ({ state, setFilter, clearFilter }) => {
    // Three independent open states so transitions across breakpoints can't
    // leak between bottom drawer, lateral drawer, popover.
    const [bottomDrawerOpen, setBottomDrawerOpen] = useState(false);
    const [lateralDrawerOpen, setLateralDrawerOpen] = useState(false);
    const [popoverOpen, setPopoverOpen] = useState(false);

    // Hooks instantiated once — each self-syncs from `state` via useEffect.
    // Operation only feeds the xs bottom drawer; at sm+ the segmented control
    // stays visible in TopBar with its own instant-commit behavior.
    const operationDraft = useOperationDraft(state);
    const priceDraft = usePriceDraft(state);
    const bedsBathsDraft = useBedsBathsDraft(state);
    const propertyTypeDraft = usePropertyTypeDraft(state);
    const ownDraft = useMoreFiltersDraft(state);

    // Standalone popover (lg+) — just own sections.
    const popoverApply = () => {
        ownDraft.commit(setFilter, clearFilter);
        setPopoverOpen(false);
    };
    const popoverClear = () => {
        ownDraft.reset(setFilter, clearFilter);
        setPopoverOpen(false);
    };

    // Three pill-active variants based on which other filter affordances are
    // visible at that breakpoint:
    //   Popover (lg+): only own sections. Other 3 pills sit alongside.
    //   Lateral drawer (sm-lg): own + 3 absorbed pills, but NOT operation
    //     (operation segmented is still visible in TopBar at this width).
    //   Bottom drawer (xs): own + 3 absorbed pills + operation (every other
    //     filter affordance is hidden so this pill is the only visible signal).
    const ownActive = [state.groundSize.length > 0, state.includeMode !== "both"];
    const absorbedPillActive = [
        state.price.length > 0,
        state.bedrooms.length > 0,
        state.bathrooms.length > 0,
        state.halfBathrooms.length > 0,
        state.parkingLots.length > 0,
        // Page-default-aware: a type page's own preset doesn't bump the badge.
        propertyTypeDraft.typeActive,
    ];
    // D18.a: operation is "active" when anything other than the default Comprar
    // is selected — includes explicit Todas (`[]` or legacy length 3), Rentar,
    // or Preventa. Default-state Comprar stays unhighlighted (no badge bump).
    const operationActive = !(
        state.operation.length === 1 && state.operation[0] === DEFAULT_OPERATION
    );
    const popoverActiveCount = ownActive.filter(Boolean).length;
    const lateralDrawerActiveCount = [...ownActive, ...absorbedPillActive].filter(Boolean).length;
    const bottomDrawerActiveCount = [...ownActive, ...absorbedPillActive, operationActive].filter(
        Boolean
    ).length;
    const popoverActive = popoverActiveCount > 0;
    const lateralDrawerActive = lateralDrawerActiveCount > 0;
    const bottomDrawerActive = bottomDrawerActiveCount > 0;
    const popoverLabel = popoverActive ? `Más filtros (${popoverActiveCount})` : "Más filtros";
    const lateralDrawerLabel = lateralDrawerActive
        ? `Más filtros (${lateralDrawerActiveCount})`
        : "Más filtros";

    // Faders start icon on every variant so "Más filtros" has a consistent
    // visual identity across breakpoints AND visually distinguishes itself
    // from the specific-filter pills (Precio/Recámaras/Tipo) when all four
    // sit side-by-side at lg+ — icon = settings bucket, no icon = specific
    // category. Chevron-down only on the popover variant since only that
    // case is genuinely a dropdown.
    const fadersIcon = (
        <Icon mr={2} boxSize={4} opacity={0.7}>
            <FadersIcon weight="bold" />
        </Icon>
    );
    // At xs the trigger sits next to the search input on a shared row. When
    // no filters are active it's a pure square IconButton (symmetric, no
    // wasted padding from a label-less icon). When there's a count, swap to
    // a Button so we can append "(n)" next to the icon without breaking
    // IconButton's icon-only contract.
    const bottomDrawerTriggerContent = (
        <>
            {fadersIcon}({bottomDrawerActiveCount})
        </>
    );
    const lateralDrawerTriggerContent = (
        <>
            {fadersIcon}
            {lateralDrawerLabel}
        </>
    );
    const popoverTriggerContent = (
        <>
            {fadersIcon}
            {popoverLabel}
            <Icon ml={2} boxSize={3} opacity={0.7}>
                <CaretDownIcon weight="bold" />
            </Icon>
        </>
    );

    const absorbedProps = {
        priceDraft,
        bedsBathsDraft,
        propertyTypeDraft,
        ownDraft,
        state,
        setFilter,
        clearFilter,
    };
    // Bottom drawer at xs gets the operation segmented absorbed too; the
    // lateral drawer at sm-lg doesn't (operation stays in TopBar there).
    const bottomDrawerProps = { ...absorbedProps, operationDraft };

    return (
        <>
            {/* xs–sm (<md = <768px): bottom drawer absorbs Operation +
                Price/Beds/Type sections. Icon-only trigger when empty,
                label-with-count when active — minimal footprint on phones
                and small tablets in portrait. */}
            <Box hideFrom="md">
                <Drawer.Root
                    open={bottomDrawerOpen}
                    onOpenChange={(e) => setBottomDrawerOpen(e.open)}
                    size="xl"
                    placement="bottom"
                >
                    <Drawer.Trigger asChild>
                        {bottomDrawerActiveCount > 0 ? (
                            <Button
                                {...triggerProps(bottomDrawerActive)}
                                minW="auto"
                                px={3}
                                aria-label="Filtros"
                            >
                                {bottomDrawerTriggerContent}
                            </Button>
                        ) : (
                            <IconButton
                                aria-label="Filtros"
                                {...triggerProps(bottomDrawerActive)}
                                minW="40px"
                                w="40px"
                                px={0}
                            >
                                <Icon boxSize={4}>
                                    <FadersIcon weight="bold" />
                                </Icon>
                            </IconButton>
                        )}
                    </Drawer.Trigger>
                    <Portal>
                        <Drawer.Backdrop />
                        <Drawer.Positioner>
                            <Drawer.Content colorPalette="accent">
                                <AbsorbedDrawerContents
                                    onClose={() => setBottomDrawerOpen(false)}
                                    {...bottomDrawerProps}
                                />
                            </Drawer.Content>
                        </Drawer.Positioner>
                    </Portal>
                </Drawer.Root>
            </Box>

            {/* md-lg (768-991): lateral drawer slides from right, same
                absorbed content as the bottom variant. Label-with-count
                trigger — landscape tablets have the room. */}
            <Box hideBelow="md" hideFrom="lg">
                <Drawer.Root
                    open={lateralDrawerOpen}
                    onOpenChange={(e) => setLateralDrawerOpen(e.open)}
                    size="sm"
                    placement="end"
                >
                    <Drawer.Trigger asChild>
                        <Button {...triggerProps(lateralDrawerActive)}>
                            {lateralDrawerTriggerContent}
                        </Button>
                    </Drawer.Trigger>
                    <Portal>
                        <Drawer.Backdrop />
                        <Drawer.Positioner>
                            <Drawer.Content colorPalette="accent">
                                <AbsorbedDrawerContents
                                    onClose={() => setLateralDrawerOpen(false)}
                                    {...absorbedProps}
                                />
                            </Drawer.Content>
                        </Drawer.Positioner>
                    </Portal>
                </Drawer.Root>
            </Box>

            {/* lg+ (≥992): standard popover with just Superficie + Incluir —
                Price/Beds/Type pills are visible in TopBar with their own
                popovers. */}
            <Box hideBelow="lg">
                <Popover.Root open={popoverOpen} onOpenChange={(e) => setPopoverOpen(e.open)}>
                    <Popover.Trigger asChild>
                        <Button {...triggerProps(popoverActive)}>{popoverTriggerContent}</Button>
                    </Popover.Trigger>
                    <Portal>
                        <Popover.Positioner>
                            <Popover.Content colorPalette="accent" w="380px" shadow="lg">
                                <Popover.Body p={4}>
                                    <Section
                                        title="Superficie del terreno (m²)"
                                        isDirty={ownDraft.sizeDirty}
                                    >
                                        <SuperficieFilterBody draft={ownDraft} />
                                    </Section>
                                    <Separator my={5} />
                                    <Section title="Incluir" isDirty={ownDraft.incluirDirty}>
                                        <IncluirFilterBody draft={ownDraft} />
                                    </Section>
                                    <Flex
                                        justify="space-between"
                                        gap={2}
                                        borderTopWidth="1px"
                                        borderColor="gray.100"
                                        pt={3}
                                        mt={4}
                                    >
                                        <Button
                                            onClick={popoverClear}
                                            variant="ghost"
                                            size="sm"
                                            px={5}
                                            borderRadius="md"
                                            color="gray.600"
                                        >
                                            Limpiar todos
                                        </Button>
                                        <Button
                                            onClick={popoverApply}
                                            size="sm"
                                            px={6}
                                            borderRadius="md"
                                            bg="colorPalette.solid"
                                            color="white"
                                            _hover={{ bg: "colorPalette.emphasized" }}
                                        >
                                            Aplicar
                                        </Button>
                                    </Flex>
                                </Popover.Body>
                            </Popover.Content>
                        </Popover.Positioner>
                    </Portal>
                </Popover.Root>
            </Box>
        </>
    );
};

export default MoreFiltersPopover;
